import {
  FULL_PATIENT_INTAKE_SECTIONS,
  type PatientIntakeAnswersFromDraftOptions,
  type PatientIntakeSections,
  type PatientQuestionnaireDraftIssue,
  type PatientQuestionnaireSection,
  patientQuestionnaireDraftIssues,
} from "@emr/contracts";
import type { IntakeData } from "../types";

/**
 * A questionnaire step: one per section the link asks for, then Review. Consent is not a step:
 * the agreement to the clinic's terms sits on Review, and only when the appointment has none
 * yet (PA-11).
 */
export type QuestionnaireStepKey = PatientQuestionnaireSection | "review";

export interface QuestionnaireStep {
  readonly key: QuestionnaireStepKey;
  /** The step's name in the step indicator, and in a submit error's prefix. */
  readonly label: string;
}

const questionnaireSteps: Record<QuestionnaireStepKey, QuestionnaireStep> = {
  identity: { key: "identity", label: "About you" },
  coverage: { key: "coverage", label: "Coverage" },
  health: { key: "health", label: "Health" },
  review: { key: "review", label: "Review" },
};

/**
 * The steps a link's questionnaire shows, in order: the sections it asks for (every one unless
 * the session says otherwise), then Review. A walk-in whose chart existed before the visit is
 * not asked About you (PA-5), so its questionnaire starts at Coverage.
 */
export function stepsFor(
  sections: PatientIntakeSections = FULL_PATIENT_INTAKE_SECTIONS,
): readonly QuestionnaireStep[] {
  return [...sections.map((section) => questionnaireSteps[section]), questionnaireSteps.review];
}

/** What the patient sees when the server, or a request's own schema, refuses the answers. */
export const answersNotAccepted =
  "Some answers could not be accepted. Check them and try again, or ask the clinic for help.";

/**
 * What the patient sees when the appointment changed under an open questionnaire (cancelled,
 * checked in, or already answered): no change to the answers can fix that.
 */
export const appointmentChanged = "This appointment has changed. Ask the clinic for a new link.";

/** True for a schema failure (a ZodError or anything carrying its `issues`), whose text is not for patients. */
export function isSchemaError(error: unknown): boolean {
  return error instanceof Error && "issues" in error;
}

/**
 * The problems that keep this step from continuing, in the shared contract's own words, so a
 * step that passes here is never refused at submit. Only the sections in `options.sections` are
 * checked; the Review step adds none of its own.
 */
export function validateStep(
  step: QuestionnaireStepKey,
  intake: IntakeData,
  options: PatientIntakeAnswersFromDraftOptions = {},
): string[] {
  const messages = patientQuestionnaireDraftIssues(intake, options)
    .filter((issue) => issue.section === step)
    .map((issue) => issue.message);
  return [...new Set(messages)];
}

/** Every remaining problem at submit, each named by the step that fixes it. */
export function submissionErrors(issues: readonly PatientQuestionnaireDraftIssue[]): string[] {
  return [
    ...new Set(
      issues.map((issue) => `${questionnaireSteps[issue.section].label}: ${issue.message}`),
    ),
  ];
}
